import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@rickylandino/react-messages';
import { Table, Modal } from 'antd';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import NumberFormat from 'react-number-format';
import Moment from 'moment';
import { CheckCircle } from 'lucide-react';
import Globals from '../../../../config/globals';
import {
    useExamPerformFailuresFiltered,
    useExamPerformFailuresFilteredCount,
    useDismissExamPerformFailure,
    invalidateAllWidgetQueries
} from '../../../../hooks/useWidgetData';
import { useSaveWidgetFilters } from '../../../../hooks/useDashboard';
import { useSelectedDashboardFacility } from '../../../../hooks/useSelectedDashboardFacility';
import ReconcilePerformFailureModal from '../ExamPerformFailures/ReconcilePerformFailureModal';

/**
 * ExamPerformFailuresFilteredWidget component
 * User-level widget for exam perform failures filtered by date range and facility.
 * Waits for the user to configure filters and click Generate Report.
 *
 * @param {Object} props
 * @param {boolean} [props.isCollapsed=false]
 * @param {boolean} [props.isExpanded=false]
 * @param {Function} [props.setIsLoading]
 * @param {Function} [props.setSharedState]
 * @param {Object} [props.widget]
 */
export default function ExamPerformFailuresFilteredWidget({
    isCollapsed = false,
    isExpanded = false,
    setIsLoading,
    setSharedState,
    widget
}) {
    const saveFiltersMutation = useSaveWidgetFilters();
    const queryClient = useQueryClient();
    const dismissMutation = useDismissExamPerformFailure();
    const isInitialLoad = useRef(true);

    const { facilities: allFacilities, selectedFacilities } = useSelectedDashboardFacility();
    const [selectedDates, setSelectedDates] = useState({ startDate: null, endDate: null });
    const [queryParams, setQueryParams] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const [showReconcileModal, setShowReconcileModal] = useState(false);
    const [failureToReconcile, setFailureToReconcile] = useState(null);
    const [showDismissModal, setShowDismissModal] = useState(false);
    const [dismissTarget, setDismissTarget] = useState(null);

    // Parse saved filter config from widget definition
    const savedFilters = useMemo(() => {
        if (!widget?.filterConfig) return null;
        if (typeof widget.filterConfig === 'string') {
            try { return JSON.parse(widget.filterConfig); } catch { return null; }
        }
        return widget.filterConfig;
    }, [widget?.filterConfig]);

    // Build params for collapsed count (only once a global facility is selected)
    const filtersConfigured = selectedFacilities.length > 0;

    const collapsedCountParams = useMemo(() => {
        if (!filtersConfigured) return null;
        return {
            startDate: savedFilters?.startDate || '',
            endDate: savedFilters?.endDate || '',
            ianaTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            selectedFacilities
        };
    }, [savedFilters, filtersConfigured, selectedFacilities]);

    const { data: collapsedCountData, isLoading: isLoadingCollapsedCount } = useExamPerformFailuresFilteredCount(
        collapsedCountParams,
        isCollapsed && !isExpanded && filtersConfigured
    );

    // Build active request with pagination
    const activeRequest = useMemo(() => {
        if (!queryParams) return null;
        return { ...queryParams, page: currentPage, pageSize };
    }, [queryParams, currentPage, pageSize]);

    const shouldLoadData = !isCollapsed || isExpanded;

    const {
        data,
        isLoading,
        isFetching,
        isError,
        error,
        refetch
    } = useExamPerformFailuresFiltered(activeRequest, shouldLoadData && activeRequest !== null);

    const results = data?.data || [];
    const totalCount = data?.totalCount || 0;
    const collapsedCount = collapsedCountData?.count ?? 0;

    useEffect(() => {
        if (setIsLoading && isExpanded) setIsLoading(isLoading || isFetching);
    }, [isLoading, isFetching, isExpanded, setIsLoading]);

    useEffect(() => {
        if (shouldLoadData && isError) {
            toast.error('Failed to fetch exam perform failures. Please try again.');
        }
    }, [shouldLoadData, isError, error]);

    useEffect(() => {
        if (setSharedState && refetch) setSharedState(prev => ({ ...prev, refetch }));
    }, [setSharedState, refetch]);

    useEffect(() => {
        if (setSharedState) setSharedState(prev => ({ ...prev, hasData: queryParams !== null || filtersConfigured }));
    }, [queryParams, filtersConfigured, setSharedState]);

    // Restore saved date filters on first load. Facility now comes from the global
    // dashboard facility selector, not per-widget saved config.
    useEffect(() => {
        if (!isInitialLoad.current || !savedFilters) return;
        isInitialLoad.current = false;
        if (savedFilters.startDate || savedFilters.endDate) {
            setSelectedDates({
                startDate: savedFilters.startDate ? Moment(savedFilters.startDate).toDate() : null,
                endDate: savedFilters.endDate ? Moment(savedFilters.endDate).toDate() : null
            });
        }
    }, [savedFilters]);

    // Auto-run when transitioning from collapsed once a global facility is selected
    useEffect(() => {
        if ((isCollapsed && !isExpanded) || queryParams !== null) return;
        if (selectedFacilities.length === 0) return;
        setQueryParams({
            startDate: savedFilters?.startDate || '',
            endDate: savedFilters?.endDate || '',
            ianaTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            selectedFacilities
        });
    }, [isCollapsed, isExpanded, savedFilters, queryParams, selectedFacilities]);

    const handleDateChange = useCallback((name, date) => {
        setSelectedDates(prev => ({ ...prev, [name]: date }));
    }, []);

    const handleSubmit = useCallback((e) => {
        e?.preventDefault();
        if (selectedFacilities.length === 0) {
            toast.error('Please select a facility using the Facility selector above.');
            return;
        }
        const startDate = selectedDates.startDate ? Moment(selectedDates.startDate).format('YYYY-MM-DD') : null;
        const endDate = selectedDates.endDate ? Moment(selectedDates.endDate).format('YYYY-MM-DD') : null;
        const params = {
            startDate: startDate || '',
            endDate: endDate || '',
            ianaTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            selectedFacilities
        };
        if (widget?.userDashboardWidgetId) {
            saveFiltersMutation.mutate({
                userDashboardWidgetId: widget.userDashboardWidgetId,
                filterConfig: { startDate, endDate, facilityIds: selectedFacilities }
            });
        }
        setCurrentPage(1);
        queryClient.invalidateQueries({ queryKey: ['widgetData', 'exam-perform-failures-filtered'] });
        setQueryParams(params);
    }, [selectedDates, selectedFacilities, widget?.userDashboardWidgetId, saveFiltersMutation, queryClient]);

    const handleReconcile = useCallback((row) => {
        setFailureToReconcile(row);
        setShowReconcileModal(true);
    }, []);

    const handleDismiss = useCallback((row) => {
        setDismissTarget(row);
        setShowDismissModal(true);
    }, []);

    const handleConfirmDismiss = useCallback(async () => {
        if (!dismissTarget?.examPerformFailureId) return;
        try {
            await dismissMutation.mutateAsync(dismissTarget.examPerformFailureId);
            setShowDismissModal(false);
            setDismissTarget(null);
            toast.success('Failure dismissed');
        } catch (e) {
            console.error('Dismiss error:', e);
            toast.error('Failed to dismiss. Please try again.');
        }
    }, [dismissTarget, dismissMutation]);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return '�';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'numeric', day: 'numeric', year: 'numeric'
        });
    }, []);

    const pink = Globals.theme === 'dark' ? '#ec4899' : '#db2777';
    const pinkHover = Globals.theme === 'dark' ? '#db2777' : '#be185d';

    const columns = useMemo(() => [
        {
            title: 'Accession #',
            dataIndex: 'accessionNumber',
            key: 'accessionNumber',
            width: 160,
            render: (val) => val || '-'
        },
        {
            title: 'Patient',
            dataIndex: 'patientName',
            key: 'patientName',
            width: 160,
            render: (name, row) => name || row.patientMrn || '-'
        },
        {
            title: 'Facility',
            dataIndex: 'facilityName',
            key: 'facilityName',
            width: 160,
        },
        {
            title: 'Exam Type',
            dataIndex: 'examTypeName',
            key: 'examTypeName',
            width: 180,
            render: (val) => val || '-'
        },
        {
            title: 'Failure Reason',
            dataIndex: 'failureReason',
            key: 'failureReason',
            width: 220,
        },
        {
            title: 'Status Code',
            dataIndex: 'statusCode',
            key: 'statusCode',
            width: 100,
            render: (val) => val ?? '-'
        },
        {
            title: 'Date',
            dataIndex: 'createdDate',
            key: 'createdDate',
            width: 120,
            render: (d) => formatDate(d)
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 180,
            fixed: 'right',
            render: (_, row) => (
                <div style={{ display: 'flex', gap: 8 }}>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleReconcile(row); }}
                        className="px-3 py-1.5 rounded-lg font-medium text-sm"
                        style={{ backgroundColor: pink, color: 'white', border: 'none', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = pinkHover}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = pink}
                    >
                        Reconcile
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleDismiss(row); }}
                        className="px-3 py-1.5 rounded-lg font-medium text-sm"
                        style={{
                            backgroundColor: 'transparent',
                            color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a',
                            border: `1px solid ${Globals.theme === 'dark' ? '#52525b' : '#d1d5db'}`,
                            cursor: 'pointer'
                        }}
                    >
                        Dismiss
                    </button>
                </div>
            )
        }
    ], [formatDate, handleReconcile, handleDismiss, pink, pinkHover]);

    // COLLAPSED STATE
    if (isCollapsed && !isExpanded) {
        if (filtersConfigured) {
            if (isLoadingCollapsedCount) {
                return (
                    <div className="flex items-center justify-center py-4">
                        <div
                            className="animate-spin rounded-full h-8 w-8 border-b-2"
                            style={{ borderColor: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}
                        />
                    </div>
                );
            }
            return (
                <div className="flex flex-col items-center justify-center py-4">
                    <div className="text-5xl font-light mb-2" style={{ color: pink }}>
                        {collapsedCount.toLocaleString()}
                    </div>
                    <div className="text-sm" style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>
                        {collapsedCount === 1 ? 'perform failure' : 'perform failures'}
                    </div>
                </div>
            );
        }
        return (
            <div className="flex flex-col items-center justify-center py-4">
                <p className="text-sm" style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>
                    Configure filters to see count
                </p>
            </div>
        );
    }

    // EXPANDED STATE
    return (
        <>
            {/* Filter form */}
            <form onSubmit={handleSubmit} className="space-y-3 mb-4">
                <div className="flex flex-wrap gap-4 items-end">
                    <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>
                            Start Date
                        </label>
                        <DatePicker
                            selected={selectedDates.startDate}
                            onChange={(date) => handleDateChange('startDate', date)}
                            className="form-control-custom"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            isClearable
                            placeholderText="MM/DD/YYYY"
                            customInput={<NumberFormat format="##/##/####" mask="_" />}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>
                            End Date
                        </label>
                        <DatePicker
                            selected={selectedDates.endDate}
                            onChange={(date) => handleDateChange('endDate', date)}
                            className="form-control-custom"
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            isClearable
                            placeholderText="MM/DD/YYYY"
                            customInput={<NumberFormat format="##/##/####" mask="_" />}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium mb-1" style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>
                            Facility
                        </label>
                        <p className="text-sm font-medium">
                            {allFacilities.find(f => f.facilityModel.facilityID === selectedFacilities[0])?.facilityModel.facilityName
                                || 'Select a facility using the selector above'}
                        </p>
                    </div>
                    <button
                        type="submit"
                        className="px-4 py-1.5 rounded-lg font-medium text-sm"
                        style={{ backgroundColor: pink, color: 'white', border: 'none', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = pinkHover}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = pink}
                    >
                        Generate Report
                    </button>
                </div>
            </form>

            {/* Empty state before first run */}
            {!queryParams && (
                <div className="flex flex-col items-center justify-center py-8">
                    <p className="text-sm" style={{ color: Globals.theme === 'dark' ? '#a1a1aa' : '#71717a' }}>
                        Select filters and click Generate Report to load data.
                    </p>
                </div>
            )}

            {/* Empty state after run with no results */}
            {queryParams && !isLoading && results.length === 0 && !isFetching && (
                <div className="flex flex-col items-center justify-center py-4">
                    <CheckCircle className="h-12 w-12 mx-auto mb-3" style={{ color: pink }} />
                    <p style={{ color: pink }}>No Exam Perform Failures Found</p>
                </div>
            )}

            {/* Results table */}
            {queryParams && (results.length > 0 || isLoading || isFetching) && (
                <Table
                    columns={columns}
                    dataSource={results}
                    rowKey="examPerformFailureId"
                    pagination={{
                        current: currentPage,
                        pageSize,
                        total: totalCount,
                        onChange: (page, newPageSize) => {
                            setCurrentPage(page);
                            if (newPageSize !== pageSize) {
                                setPageSize(newPageSize);
                                setCurrentPage(1);
                            }
                        },
                        pageSizeOptions: ['25', '50', '100', '200'],
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} failures`
                    }}
                    size="small"
                    scroll={{ y: 450 }}
                    virtual
                    loading={{ spinning: isLoading || isFetching }}
                />
            )}

            <ReconcilePerformFailureModal
                visible={showReconcileModal}
                failure={failureToReconcile}
                onSuccess={() => {
                    setShowReconcileModal(false);
                    setFailureToReconcile(null);
                }}
                onCancel={() => {
                    setShowReconcileModal(false);
                    setFailureToReconcile(null);
                }}
            />

            <Modal
                visible={showDismissModal}
                title="Dismiss Perform Failure"
                onCancel={() => { setShowDismissModal(false); setDismissTarget(null); }}
                footer={[
                    <button
                        key="cancel"
                        onClick={() => { setShowDismissModal(false); setDismissTarget(null); }}
                        className="px-4 py-1.5 rounded-lg font-medium text-sm"
                        style={{
                            backgroundColor: 'transparent',
                            border: `1px solid ${Globals.theme === 'dark' ? '#52525b' : '#d1d5db'}`,
                            cursor: 'pointer',
                            marginRight: 8
                        }}
                    >
                        Cancel
                    </button>,
                    <button
                        key="confirm"
                        onClick={handleConfirmDismiss}
                        disabled={dismissMutation.isLoading}
                        className="px-4 py-1.5 rounded-lg font-medium text-sm"
                        style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', cursor: 'pointer' }}
                    >
                        Dismiss
                    </button>
                ]}
            >
                <p>
                    Dismissing accession <strong>{dismissTarget?.accessionNumber || '(no accession)'}</strong> will
                    remove it from the queue without updating any exam data.
                </p>
                <p>Are you sure?</p>
            </Modal>
        </>
    );
}
